import React, { useState, useEffect, useCallback } from 'react';
import CancelOffersGeneralDataComponent from './CancelOffersGeneralDataComponent';
import CancelOffersListComponent from './CancelOffersListComponent';
import * as queries from '../queries';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { UPDATE_CANCEL_OFFER_PARAMETER } from '../mutations';
import { Grid } from '@mui/material';
import { emptyObject } from 'utils/constants';
import PropTypes from 'prop-types';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OFFER_CANCELLED_STATES_PARAM_UPDATED } from '../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { useApolloClient } from '@apollo/client';
import { offerCancelledStatesValidator, offerCancelledConditionValidator } from 'features/cancelOffers/validators';
import { useValidation } from '@totalsoft/pure-validations-react';
import { isValid } from '@totalsoft/pure-validations-react';
import { referenceDates } from '../constants';
import { offerDocumentTypeId } from '../constants';
import { DOCUMENT_TYPE_WITH_ALLOWED_STATES_QUERY } from '../../../features/documentTypes/components/edit/allowedStates/list/queries';
import {
  ALLOWED_STATE_QUERY,
  DOCUMENT_STATES_LIST_QUERY
} from '../../../features/documentTypes/components/edit/allowedStates/edit/queries';
import WrappedFakeText from 'components/common/WrappedFakeText';

const CancelOffersContainer = ({ onSaveFnChanged }) => {
  const [t] = useTranslation();
  const addToast = useToast();
  const [activeDocumentTypes, setActiveDocumentTypes] = useState([]);
  const [documentStateList, setDocumentStateList] = useState([]);
  const [offerCancellationConfiguration, setOfferCancelledConditions] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const client = useApolloClient();

  const [offerCancelledStatesValidation, validateOfferCancelledStatesParam] = useValidation(offerCancelledStatesValidator);

  const [offerCancelledConditionValidation, validateOfferCancelledCondition] = useValidation(offerCancelledConditionValidator);

  useQuery(queries.ACTIVE_DOCUMENTS_QUERY, {
    onCompleted: data => {
      setActiveDocumentTypes(data?.activeDocumentTypes);
    }
  });

  useQuery(DOCUMENT_STATES_LIST_QUERY, {
    onCompleted: data => {
      setDocumentStateList(data.documentStatesList);
    }
  });

  const { loading: loadingConditions } = useQuery(queries.OFFER_CANCELLED_CONDITION_QUERY, {
    onCompleted: data => {
      setOfferCancelledConditions(data?.offerCancelledCondition || emptyObject);
    }
  });

  const { data: offerStateList } = useQuery(DOCUMENT_TYPE_WITH_ALLOWED_STATES_QUERY, {
    variables: {
      id: parseInt(offerDocumentTypeId)
    },
    notifyOnNetworkStatusChange: true
  });

  const getAllowedStateId = () => {
    return offerStateList?.documentTypeWithAllowedStates?.allowedStates.find(
      x => x.documentStateId === offerCancellationConfiguration.cancelledStateId
    )?.id;
  };

  const { data: allowedStateData } = useQuery(ALLOWED_STATE_QUERY, {
    variables: {
      id: getAllowedStateId()
    },
    notifyOnNetworkStatusChange: true,
    skip: getAllowedStateId() === undefined
  });

  const [addOrUpdateCancelOfferParameter] = useMutation(UPDATE_CANCEL_OFFER_PARAMETER);

  const onHeaderPropertyChanged = useCallback((propertyName, newPropertyValue) => {
    setIsDirty(true);
    setOfferCancelledConditions(prevState => ({ ...prevState, [propertyName]: newPropertyValue }));
  }, []);

  const onHeaderLookupPropertyChange = useCallback(
    (propertyId, propertyCode, newPropertyValueId, newPropertyValueCode) => {
      setIsDirty(true);
      if (newPropertyValueId === undefined) {
        newPropertyValueId = null;
        newPropertyValueCode = null;
      }
      if (propertyId === 'cancelledStateId') {
        let localConditions = offerCancellationConfiguration.offerCancelledConditions.slice();
        localConditions.forEach(el => {
          el.reasonId = null;
          el.reasonCode = null;
        });
        setOfferCancelledConditions(prevState => ({
          ...prevState,
          [propertyId]: newPropertyValueId,
          [propertyCode]: newPropertyValueCode,
          offerReasonCodeForExpiredFolder: null,
          offerReasonIdForExpiredFolder: null,
          offerCancelledConditions: localConditions
        }));
      } else {
        setOfferCancelledConditions(prevState => ({
          ...prevState,
          [propertyId]: newPropertyValueId,
          [propertyCode]: newPropertyValueCode
        }));
      }
    },
    [offerCancellationConfiguration]
  );

  const handlePropertyChange = useCallback(
    (propertyName, newPropertyValue, index) => {
      setIsDirty(true);
      let localConditions = offerCancellationConfiguration.offerCancelledConditions.slice();
      if (newPropertyValue === '') newPropertyValue = null;
      const newCondition = { ...localConditions[index], [propertyName]: newPropertyValue };
      localConditions[index] = newCondition;
      setOfferCancelledConditions(prevState => ({ ...prevState, offerCancelledConditions: localConditions }));
    },
    [offerCancellationConfiguration.offerCancelledConditions]
  );

  const handleLookupPropertyChange = useCallback(
    (propertyId, propertyCode, newPropertyIdValue, newPropertyCodeValue, index, collection = null) => {
      setIsDirty(true);
      let localConditions = offerCancellationConfiguration.offerCancelledConditions.slice();
      let newCondition = {};
      if (collection === 'documentTypes') {
        newCondition = {
          ...localConditions[index],
          [propertyId]: newPropertyIdValue,
          [propertyCode]: newPropertyCodeValue,
          documentTypeCode: activeDocumentTypes.find(x => x.id === newPropertyIdValue)?.code,
          stateId: null,
          stateCode: null
        };
      } else if (collection === 'documentStates') {
        newCondition = {
          ...localConditions[index],
          [propertyId]: newPropertyIdValue,
          [propertyCode]: newPropertyCodeValue,
          stateCode: offerStateList?.documentTypeWithAllowedStates?.allowedStates.find(x => x.documentStateId === newPropertyIdValue)
            ?.documentStateCode
        };
      } else if (collection === 'reasons') {
        newCondition = {
          ...localConditions[index],
          [propertyId]: newPropertyIdValue,
          [propertyCode]: newPropertyCodeValue,
          reasonCode: allowedStateData?.allowedState?.reasons.find(x => x.reasonId === newPropertyIdValue)?.reasonCode
        };
      } else if (collection === 'referenceDates') {
        newCondition = {
          ...localConditions[index],
          [propertyCode]: newPropertyCodeValue
        };
      } else
        newCondition = {
          ...localConditions[index],
          [propertyId]: newPropertyIdValue,
          [propertyCode]: newPropertyCodeValue
        };

      localConditions[index] = newCondition;
      setOfferCancelledConditions(prevState => ({ ...prevState, offerCancelledConditions: localConditions }));
    },
    [
      offerCancellationConfiguration.offerCancelledConditions,
      offerStateList?.documentTypeWithAllowedStates?.allowedStates,
      activeDocumentTypes,
      allowedStateData?.allowedState?.reasons
    ]
  );

  const handleSave = useCallback(() => {
    const getDuplicateConditions = conditions => {
      const duplicates = [];
      conditions.forEach(condition => {
        conditions.forEach(c => {
          if (
            c.documentTypeCode === condition.documentTypeCode &&
            c.stateCode === condition.stateCode &&
            c.referenceDate === condition.referenceDate &&
            c !== condition
          ) {
            duplicates.push({
              documentType: activeDocumentTypes.filter(x => x.id === condition.documentTypeId).map(x => x.name),
              state: documentStateList.filter(x => x.documentStateId === condition.stateId).map(x => x.name),
              referenceDate: condition.referenceDate
            });
          }
        });
      });

      return duplicates.filter((v, i, a) => a.findIndex(v2 => JSON.stringify(v2) === JSON.stringify(v)) === i);
    };
    setIsDirty(true);
    const conditions = [];
    offerCancellationConfiguration.offerCancelledConditions.forEach(element => {
      conditions.push({
        documentTypeCode: element.documentTypeCode,
        stateCode: element.stateCode,
        normalExpiryPeriod: element.normalExpiryPeriod,
        extendedExpiryPeriod: element.extendedExpiryPeriod,
        referenceDate: element.referenceDate,
        reasonCode: element.reasonCode
      });
    });

    const duplicates = getDuplicateConditions(conditions);

    if (duplicates.length > 0) {
      let message = t('CancelOffers.MessageError');
      duplicates.forEach(duplicate => {
        message += t('CancelOffers.DuplicateConditions', {
          data: {
            documentType: duplicate.documentType,
            state: duplicate.state,
            referenceDate: duplicate.referenceDate
          }
        });
      });
      addToast(message, 'error');
      return;
    }

    const input = {
      checkExpiredFolders: offerCancellationConfiguration.checkExpiredFolders,
      offerReasonCodeForExpiredFolder: offerCancellationConfiguration.offerReasonCodeForExpiredFolder,
      cancelledStateCode: offerCancellationConfiguration.cancelledStateCode,
      offerCancelledConditions: conditions
    };

    setSaving(true);
    addOrUpdateCancelOfferParameter({ variables: { input } });
  }, [
    offerCancellationConfiguration.offerCancelledConditions,
    offerCancellationConfiguration.checkExpiredFolders,
    offerCancellationConfiguration.offerReasonCodeForExpiredFolder,
    offerCancellationConfiguration.cancelledStateCode,
    addOrUpdateCancelOfferParameter,
    activeDocumentTypes,
    documentStateList,
    t,
    addToast
  ]);

  const handleAdd = useCallback(() => {
    const emptyItem = {
      documentTypeId: null,
      documentTypeName: null,
      documentTypeCode: null,
      stateId: null,
      stateCode: null,
      normalExpiryPeriod: null,
      extendedExpiryPeriod: null,
      referenceDate: null,
      reasonCode: null
    };
    setIsDirty(true);
    setOfferCancelledConditions(prevState => ({
      ...prevState,
      offerCancelledConditions: [emptyItem, ...(prevState.offerCancelledConditions || [])]
    }));
  }, []);

  const handleDelete = useCallback(
    index => {
      setIsDirty(true);
      const conditions = offerCancellationConfiguration.offerCancelledConditions.slice();
      conditions.splice(index, 1);
      setOfferCancelledConditions(prevState => ({ ...prevState, offerCancelledConditions: conditions }));
    },
    [offerCancellationConfiguration.offerCancelledConditions]
  );

  useEffect(() => {
    if (!loadingConditions) validateOfferCancelledStatesParam(offerCancellationConfiguration);
  }, [offerCancellationConfiguration, validateOfferCancelledStatesParam, loadingConditions]);

  useEffect(() => {
    if (!loadingConditions) validateOfferCancelledCondition(offerCancellationConfiguration.offerCancelledConditions);
  }, [offerCancellationConfiguration.offerCancelledConditions, validateOfferCancelledCondition, loadingConditions]);

  useEffect(() => {
    onSaveFnChanged(
      handleSave,
      !isDirty || saving || !(offerCancelledStatesValidation |> isValid) || !(offerCancelledConditionValidation |> isValid)
    );
  }, [handleSave, isDirty, onSaveFnChanged, saving, offerCancelledStatesValidation, offerCancelledConditionValidation]);

  useSubscription(OFFER_CANCELLED_STATES_PARAM_UPDATED, {
    onData: _ => {
      setIsDirty(false);
      setSaving(false);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'activeDocumentTypes' });
      client.cache.gc();
      addToast(t('CancelOffers.SavingSucceeded'), 'success');
    }
  });

  return (
    <>
      {loadingConditions ? (
        <WrappedFakeText onPaper />
      ) : (
        <Grid>
          <Grid container item xs={12} sm={12} justifyContent='flex-end'>
            <Prompt when={isDirty} message={t('CancelOffers.LeavingWithoutSaving')} />
          </Grid>
          <CancelOffersListComponent
            documentTypes={activeDocumentTypes}
            conditions={offerCancellationConfiguration.offerCancelledConditions}
            reasons={allowedStateData?.allowedState?.reasons}
            referenceDates={referenceDates}
            handleAdd={handleAdd}
            onDelete={handleDelete}
            onPropertyChanged={handlePropertyChange}
            onLookupPropertyChanged={handleLookupPropertyChange}
            validation={offerCancelledConditionValidation}
          />
          <CancelOffersGeneralDataComponent
            offerCancelledStates={offerCancellationConfiguration}
            reasons={allowedStateData?.allowedState?.reasons}
            stateList={offerStateList?.documentTypeWithAllowedStates?.allowedStates}
            onPropertyChanged={onHeaderPropertyChanged}
            onLookupPropertyChanged={onHeaderLookupPropertyChange}
            validation={offerCancelledStatesValidation}
          />
        </Grid>
      )}
    </>
  );
};

CancelOffersContainer.propTypes = {
  onSaveFnChanged: PropTypes.func.isRequired
};

export default CancelOffersContainer;
